/**
 * DesmosCalculatorOverlay - Global graphing calculator overlay
 *
 * Provides a blank Desmos graphing calculator that students can open
 * anytime during math challenges. Mirrors the calculator available
 * on the digital SAT.
 *
 * Features:
 * - Full interactive graphing calculator
 * - Resizable/draggable overlay
 * - Dark mode support
 * - Keyboard shortcut (Ctrl+G) to toggle
 * - State persistence during session
 * - Error boundary protection
 */

import { memo, useState, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { useDesmos, useDesmosAvailable } from '../hooks/useDesmos'
import DesmosErrorBoundary from './DesmosErrorBoundary'
import { createLogger } from '../utils/logger'

const logger = createLogger('DesmosCalculatorOverlay')

// Graph icon for the button
const GraphIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16"
    />
  </svg>
)

GraphIcon.propTypes = {
  className: PropTypes.string
}

// Overlay size presets
const SIZES = {
  small: { width: 500, height: 450 },
  medium: { width: 650, height: 550 },
  large: { width: 800, height: 650 }
}

// Alignment modes
const ALIGNMENTS = {
  center: 'center',
  left: 'left',
  right: 'right',
  fullscreen: 'fullscreen'
}

// Session storage keys for persistence
const CALCULATOR_STATE_KEY = 'desmos_calculator_state'
const CALCULATOR_LAYOUT_KEY = 'desmos_calculator_layout'
const CALCULATOR_MODE_KEY = 'desmos_calculator_mode'

// Calculator modes matching SAT Bluebook experience
const CALCULATOR_MODES = {
  graphing: 'graphing',
  scientific: 'scientific'
}

/**
 * Load saved calculator mode from session storage
 */
function loadSavedMode() {
  try {
    const saved = sessionStorage.getItem(CALCULATOR_MODE_KEY)
    if (saved && Object.values(CALCULATOR_MODES).includes(saved)) {
      return saved
    }
    return CALCULATOR_MODES.graphing
  } catch {
    return CALCULATOR_MODES.graphing
  }
}

/**
 * Save calculator mode to session storage
 */
function saveMode(mode) {
  try {
    sessionStorage.setItem(CALCULATOR_MODE_KEY, mode)
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

/**
 * Load saved calculator state from session storage
 */
function loadSavedState() {
  try {
    const saved = sessionStorage.getItem(CALCULATOR_STATE_KEY)
    return saved ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

/**
 * Save calculator state to session storage
 */
function saveState(state) {
  try {
    sessionStorage.setItem(CALCULATOR_STATE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

/**
 * Load saved layout (position, size, alignment) from session storage
 */
function loadSavedLayout() {
  try {
    const saved = sessionStorage.getItem(CALCULATOR_LAYOUT_KEY)
    return saved ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

/**
 * Save layout to session storage
 */
function saveLayout(layout) {
  try {
    sessionStorage.setItem(CALCULATOR_LAYOUT_KEY, JSON.stringify(layout))
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

const DesmosCalculatorOverlayInner = memo(({ isOpen, onClose, initialSize = 'medium' }) => {
  const isDesmosAvailable = useDesmosAvailable()

  // Calculator mode state (graphing or scientific)
  const [calculatorMode, setCalculatorMode] = useState(() => loadSavedMode())
  // Saved graphing calculator state for restoring when switching back from scientific
  const savedGraphingStateRef = useRef(null)

  // Load saved layout or use defaults
  const [savedLayout] = useState(() => loadSavedLayout())

  // Default dimensions from preset
  const defaultDimensions = SIZES[initialSize] || SIZES.medium

  const [alignment, setAlignment] = useState(() => savedLayout?.alignment || ALIGNMENTS.center)
  // Store actual dimensions (width/height) instead of preset name
  const [dimensions, setDimensions] = useState(() => {
    if (savedLayout?.dimensions) {
      return savedLayout.dimensions
    }
    return { width: defaultDimensions.width, height: defaultDimensions.height }
  })
  // Calculate initial center position from saved or center of screen
  const [position, setPosition] = useState(() => {
    if (savedLayout?.position) {
      return savedLayout.position
    }
    return {
      x: Math.max(0, (window.innerWidth - defaultDimensions.width) / 2),
      y: Math.max(0, (window.innerHeight - defaultDimensions.height) / 2)
    }
  })
  const [isDragging, setIsDragging] = useState(false)
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 })
  const overlayRef = useRef(null)

  // Save layout when position, dimensions, or alignment changes
  useEffect(() => {
    saveLayout({ position, dimensions, alignment })
  }, [position, dimensions, alignment])

  // Track actual size changes from CSS resize handle
  useEffect(() => {
    if (!overlayRef.current || alignment !== ALIGNMENTS.center) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          setDimensions((prev) => {
            // Only update if significantly different (avoid loops)
            if (Math.abs(prev.width - width) > 5 || Math.abs(prev.height - height) > 5) {
              return { width: Math.round(width), height: Math.round(height) }
            }
            return prev
          })
        }
      }
    })

    resizeObserver.observe(overlayRef.current)
    return () => resizeObserver.disconnect()
  }, [alignment])

  // Load saved state on mount
  const [savedState] = useState(() => loadSavedState())

  // Determine initial state: use saved graphing state if switching back, otherwise session state
  const effectiveInitialState =
    calculatorMode === CALCULATOR_MODES.graphing
      ? savedGraphingStateRef.current || savedState
      : null

  const { containerRef, isReady, resize, reset, getState, calculator } = useDesmos({
    initialState: effectiveInitialState,
    interactive: true,
    showExpressions: true,
    showKeypad: true,
    showSettings: true,
    lockViewport: false,
    calculatorType: calculatorMode
  })

  // Save state periodically and when closing
  useEffect(() => {
    if (!isReady || !calculator) return

    // Save state when closing
    const handleSave = () => {
      const state = getState()
      if (state) {
        saveState(state)
      }
    }

    // Save every 5 seconds while open
    const interval = setInterval(handleSave, 5000)

    return () => {
      clearInterval(interval)
      handleSave() // Save on cleanup
    }
  }, [isReady, calculator, getState])

  // Handle drag start - only allow dragging in center alignment mode
  const handleDragStart = useCallback(
    (e) => {
      if (alignment !== ALIGNMENTS.center) return

      setIsDragging(true)
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y
      }
    },
    [position, alignment]
  )

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragRef.current.startX
      const deltaY = e.clientY - dragRef.current.startY

      // Calculate boundaries based on actual overlay dimensions
      const overlayWidth = overlayRef.current?.offsetWidth || 100
      const overlayHeight = overlayRef.current?.offsetHeight || 100
      const maxX = Math.max(0, window.innerWidth - overlayWidth)
      const maxY = Math.max(0, window.innerHeight - overlayHeight)

      setPosition({
        x: Math.max(0, Math.min(maxX, dragRef.current.startPosX + deltaX)),
        y: Math.max(0, Math.min(maxY, dragRef.current.startPosY + deltaY))
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  // Use ResizeObserver for resize handling
  useEffect(() => {
    if (!isReady || !isOpen || !overlayRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      resize()
    })

    resizeObserver.observe(overlayRef.current)

    return () => {
      // Perform a final resize before disconnecting to ensure proper layout
      // during the unmount transition
      try {
        resize()
      } catch (err) {
        // Log at debug level for troubleshooting - calculator may already be destroyed
        logger.debug('[DesmosCalculatorOverlay] Resize during cleanup failed:', err.message)
      }
      resizeObserver.disconnect()
    }
  }, [isReady, isOpen, resize])

  // Resize calculator when dimensions or alignment changes
  useEffect(() => {
    if (isReady && isOpen) {
      requestAnimationFrame(() => {
        resize()
      })
    }
  }, [dimensions, alignment, isReady, isOpen, resize])

  // Handle keyboard shortcut to close (Escape)
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Alignment handlers
  const setAlignLeft = useCallback(() => {
    setAlignment(ALIGNMENTS.left)
  }, [])

  const setAlignCenter = useCallback(() => {
    setAlignment(ALIGNMENTS.center)
    // Re-center position using current dimensions
    setPosition({
      x: Math.max(0, (window.innerWidth - dimensions.width) / 2),
      y: Math.max(0, (window.innerHeight - dimensions.height) / 2)
    })
  }, [dimensions])

  const setAlignRight = useCallback(() => {
    setAlignment(ALIGNMENTS.right)
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (alignment === ALIGNMENTS.fullscreen) {
      setAlignment(ALIGNMENTS.center)
      // Re-center using saved dimensions
      setPosition({
        x: Math.max(0, (window.innerWidth - dimensions.width) / 2),
        y: Math.max(0, (window.innerHeight - dimensions.height) / 2)
      })
    } else {
      setAlignment(ALIGNMENTS.fullscreen)
    }
  }, [alignment, dimensions])

  // Handle reset - clears calculator and saved state
  const handleReset = useCallback(() => {
    reset()
    sessionStorage.removeItem(CALCULATOR_STATE_KEY)
  }, [reset])

  // Handle mode switch between graphing and scientific
  const handleModeSwitch = useCallback(
    (newMode) => {
      if (newMode === calculatorMode) return

      // Save graphing state before switching away
      if (calculatorMode === CALCULATOR_MODES.graphing && calculator) {
        const state = getState()
        if (state) {
          savedGraphingStateRef.current = state
          saveState(state)
        }
      }

      setCalculatorMode(newMode)
      saveMode(newMode)
    },
    [calculatorMode, calculator, getState]
  )

  if (!isOpen) return null

  const isFullscreen = alignment === ALIGNMENTS.fullscreen
  const isLeftDocked = alignment === ALIGNMENTS.left
  const isRightDocked = alignment === ALIGNMENTS.right
  const isCentered = alignment === ALIGNMENTS.center

  // Calculate overlay style based on alignment
  // Header height offset for docked modes (to not go behind the app header)
  const HEADER_OFFSET = 56 // Approximate header height in pixels

  const getOverlayStyle = () => {
    if (isFullscreen) {
      return {
        position: 'fixed',
        left: 0,
        top: HEADER_OFFSET,
        width: '100%',
        height: `calc(100dvh - ${HEADER_OFFSET}px)`
      }
    }
    if (isLeftDocked) {
      return {
        position: 'fixed',
        left: 0,
        top: HEADER_OFFSET,
        width: dimensions.width,
        minWidth: 300,
        maxWidth: '60vw',
        height: `calc(100dvh - ${HEADER_OFFSET}px)`
      }
    }
    if (isRightDocked) {
      return {
        position: 'fixed',
        right: 0,
        top: HEADER_OFFSET,
        width: dimensions.width,
        minWidth: 300,
        maxWidth: '60vw',
        height: `calc(100dvh - ${HEADER_OFFSET}px)`
      }
    }
    // Center mode - free positioning
    return {
      position: 'fixed',
      left: position.x,
      top: position.y,
      width: dimensions.width,
      height: dimensions.height
    }
  }

  const overlayStyle = getOverlayStyle()

  // Determine resize class based on alignment
  const getResizeClass = () => {
    if (isFullscreen) return ''
    if (isLeftDocked) return 'resize-x' // Horizontal resize from right edge
    if (isRightDocked) return 'resize-x' // Horizontal resize from left edge
    return 'resize' // Full resize in center mode
  }

  const overlayContent = (
    <div
      ref={overlayRef}
      className={`z-50 bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden ${
        isCentered ? 'rounded-lg' : ''
      } ${getResizeClass()}`}
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-label={`${calculatorMode === CALCULATOR_MODES.scientific ? 'Scientific' : 'Graphing'} Calculator`}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${
          isCentered ? 'cursor-move' : ''
        }`}
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-2">
          <GraphIcon className="w-4 h-4 text-primary-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {calculatorMode === CALCULATOR_MODES.scientific ? 'Scientific' : 'Graphing'} Calculator
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">(Ctrl+G to toggle)</span>
        </div>

        <div className="flex items-center gap-1">
          {/* Reset button */}
          <button
            onClick={handleReset}
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            title="Clear calculator"
            aria-label="Clear calculator"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>

          {/* Divider */}
          <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Align Left button */}
          <button
            onClick={setAlignLeft}
            className={`p-1.5 rounded transition-colors ${
              isLeftDocked
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}
            title="Dock left"
            aria-label="Dock calculator to left"
            aria-pressed={isLeftDocked}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v16M8 8h12M8 12h8M8 16h12"
              />
            </svg>
          </button>

          {/* Align Center button */}
          <button
            onClick={setAlignCenter}
            className={`p-1.5 rounded transition-colors ${
              isCentered
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}
            title="Center (draggable)"
            aria-label="Center calculator"
            aria-pressed={isCentered}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M7 12h10M4 18h16"
              />
            </svg>
          </button>

          {/* Align Right button */}
          <button
            onClick={setAlignRight}
            className={`p-1.5 rounded transition-colors ${
              isRightDocked
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}
            title="Dock right"
            aria-label="Dock calculator to right"
            aria-pressed={isRightDocked}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 4v16M4 8h12M8 12h8M4 16h12"
              />
            </svg>
          </button>

          {/* Divider */}
          <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* Fullscreen toggle button */}
          <button
            onClick={toggleFullscreen}
            className={`p-1.5 rounded transition-colors ${
              isFullscreen
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-pressed={isFullscreen}
          >
            {isFullscreen ? (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            title="Close (Esc)"
            aria-label="Close calculator"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mode switcher */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <button
          onClick={() => handleModeSwitch(CALCULATOR_MODES.graphing)}
          className={`flex-1 px-4 py-1.5 text-sm font-medium transition-colors ${
            calculatorMode === CALCULATOR_MODES.graphing
              ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-white dark:bg-gray-900'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          role="tab"
          aria-selected={calculatorMode === CALCULATOR_MODES.graphing}
          aria-controls="calculator-panel"
        >
          Graphing
        </button>
        <button
          onClick={() => handleModeSwitch(CALCULATOR_MODES.scientific)}
          className={`flex-1 px-4 py-1.5 text-sm font-medium transition-colors ${
            calculatorMode === CALCULATOR_MODES.scientific
              ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-white dark:bg-gray-900'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          role="tab"
          aria-selected={calculatorMode === CALCULATOR_MODES.scientific}
          aria-controls="calculator-panel"
        >
          Scientific
        </button>
      </div>

      {/* Calculator container */}
      <div className="flex-1 relative" id="calculator-panel" role="tabpanel">
        {!isDesmosAvailable ? (
          <div
            className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800"
            role="status"
            aria-label="Loading calculator"
          >
            <div className="text-center text-gray-500 dark:text-gray-400">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2"
                aria-hidden="true"
              />
              <p className="text-sm">Loading calculator...</p>
            </div>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="w-full h-full"
            role="application"
            aria-label={`Desmos ${calculatorMode === CALCULATOR_MODES.scientific ? 'scientific' : 'graphing'} calculator`}
          />
        )}
      </div>
    </div>
  )

  // Render in a portal to ensure it's on top of everything
  return createPortal(
    <>
      {/* Backdrop for fullscreen mode */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} aria-hidden="true" />
      )}
      {overlayContent}
    </>,
    document.body
  )
})

DesmosCalculatorOverlayInner.displayName = 'DesmosCalculatorOverlayInner'

DesmosCalculatorOverlayInner.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialSize: PropTypes.oneOf(['small', 'medium', 'large', 'fullscreen'])
}

// Wrapped component with error boundary
const DesmosCalculatorOverlay = (props) => (
  <DesmosErrorBoundary minHeight={300}>
    <DesmosCalculatorOverlayInner {...props} />
  </DesmosErrorBoundary>
)

DesmosCalculatorOverlay.displayName = 'DesmosCalculatorOverlay'

DesmosCalculatorOverlay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialSize: PropTypes.oneOf(['small', 'medium', 'large', 'fullscreen'])
}

/**
 * Calculator toggle button component
 * Can be added to any toolbar to show/hide the calculator
 */
export const CalculatorButton = memo(({ onClick, isOpen, className = '', disabled = false }) => {
  const isDesmosAvailable = useDesmosAvailable()

  // Show button in loading or disabled state if Desmos isn't available yet
  // This is better UX than hiding the button entirely
  const isDisabled = disabled || !isDesmosAvailable

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
        isOpen
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      title={isDesmosAvailable ? 'Open Graphing Calculator (Ctrl+G)' : 'Loading calculator...'}
      aria-label={isOpen ? 'Close graphing calculator' : 'Open graphing calculator'}
      aria-pressed={isOpen}
    >
      <GraphIcon className="w-5 h-5" />
      <span className="text-sm font-medium hidden sm:inline">Calculator</span>
    </button>
  )
})

CalculatorButton.displayName = 'CalculatorButton'

CalculatorButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool
}

/**
 * Hook to manage calculator overlay state with keyboard shortcut
 */
export function useCalculatorOverlay() {
  const [isOpen, setIsOpen] = useState(false)

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Handle Ctrl+G keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
        e.preventDefault()
        toggle()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggle])

  return { isOpen, toggle, open, close }
}

export default DesmosCalculatorOverlay
